import { useState, useEffect, useRef, useMemo } from 'react';
import { Datepicker, GridContainer, GridItem, Button } from '../../../components/shared';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import { routeConstants } from '../../../constants/routeConstants';
import { useGetDatesOfReadingQuery } from '../../../services/CollectionService/CollectionService';
import { useNavigate, useLocation } from 'react-router-dom';
import { MESSAGE } from '../../../constants/message';
import {
  checkObjectNotEmpty,
  checkValueNotNullUndefinedBlank,
  dateFormatForApi,
  getDateFormat,
  isAllObjectValuesNullOrZero,
} from '../../../utils/common-methods';
import { Tooltip, Typography, useMediaQuery, useTheme } from '@mui/material';
import { Warning } from '@mui/icons-material';
import useStyles from './style';
import { useDispatch, useSelector } from 'react-redux';
import {
  setSublocationDates,
  setIntercardData,
  setSubLocationDatesFormIsDirty,
  updateFormikData as updateFormikDataForOpenCollections,
  setSublocationAssets,
  setLocationCurrency,
} from '../../../redux-slice/newcollection';
import {
  setSublocationDatesForReconciliation,
  setUpdateStatesForReconciliationSublocationDatesChange,
  setIntercardDataForReconciliation,
  setDefaultAccountSummaryState,
  updateFormikData,
  setSublocationAssets as setSublocationAssetsForReconciliation,
  setLocationCurrencyForReconciliation,
} from '../../../redux-slice/reconciliationCollection';
import { useGetCollectionsBySublocationIdQuery } from '../../../services/CollectionService/CollectionService';
import {
  getIntercardData,
  getLastCollectionDate,
  getNewAssetDataFromSublocation,
  getSeedLiveDataForUpdate,
  getPrevIntercardDataExcludingUpdatedIntercard,
  getInitialPlaycardState,
  getRevisedPlaycardValuesWithChangeInDateOfReading,
  getPlayCardSalesRevenueInUsd,
  generateFormikDataUpdates,
  getCollectionCurrencyDetails,
} from '../../../utils/collection-methods';
import { setPlayCardSalesSummary, setPlayCards } from '../../../redux-slice/playcardsSummary';
import { ROLE } from '../../../constants/roles';
import { useHasRoles } from '../../../hooks';

const SublocationDateModule = ({
  sublocation,
  originalSublocation,
  setSkip,
  setSublocation,
  skip,
  isReconciliation = false,
  hasWarning = false,
  readFromDraft,
  handleSublocationDeleteClick,
  turnOffDraft = () => {},
  saveAsDraftHandler,
  onSublocationAssetsUpdate,
  isInteractionBlocked = false,
}) => {
  const [dateOfReading, setDateOfReading] = useState(isReconciliation ? sublocation?.dateOfReading : new Date());
  const [prevDateOfReading, setPrevDateOfReading] = useState(dateOfReading);
  const [dateEntered, setDateEntered] = useState(isReconciliation ? sublocation?.dateEntered : new Date());
  const theme = useTheme();
  const isBelowLG = useMediaQuery(theme.breakpoints.down('lg'));
  const classes = useStyles();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const storedSublocationId = useRef(null);
  const sublocationID = storedSublocationId.current || sublocation.id;
  const shouldFetchHistory = Boolean(sublocationID);
  const { intercardData, sublocationDates } = useSelector((state) => state.newCollection);
  const { intercardDataForReconciliation, sublocationDatesForReconciliation, accountSummary, locationCurrency } =
    useSelector((state) => state.reconciliationCollection);
  const { draftSublocationDates } = useSelector((state) => state.storedCollections);
  const { draftSublocationDatesOfReconciliation } = useSelector((state) => state.storedReconciliationCollections);

  const { playCards } = useSelector((state) => state.playCard);

  const isNotIndependentRep = useHasRoles([
    ROLE.SUPER_ADMIN_IT,
    ROLE.EXECS,
    ROLE.ACCOUNTING,
    ROLE.MANAGER,
    ROLE.DM,
    ROLE.VP,
    ROLE.REGIONAL_MANAGER,
  ]);
  const hasDeleteSublocationAndAssetAccess = useHasRoles([ROLE.SUPER_ADMIN_IT, ROLE.ACCOUNTING]);
  const location = useLocation();
  const [initialFromApproved, setInitialFromApproved] = useState(false);
  useEffect(() => {
    if (location.state?.fromApproved) {
      setInitialFromApproved(true);
      // Clear the state to prevent affecting future navigations
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state]);

  useEffect(() => {
    if (
      isReconciliation &&
      sublocationDatesForReconciliation &&
      checkObjectNotEmpty(sublocationDatesForReconciliation) &&
      checkObjectNotEmpty(sublocationDatesForReconciliation[sublocation?.id])
    ) {
      let sublocationDatesFromState = sublocationDatesForReconciliation[sublocation?.id];
      setDateOfReading(sublocationDatesFromState?.dateOfReading);
      setDateEntered(sublocationDatesFromState?.dateEntered);
    }
    if (!isReconciliation) {
      if (sublocationDates && checkObjectNotEmpty(sublocationDates[sublocation?.id])) {
        let sublocationDatesFromState = sublocationDates[sublocation?.id];
        if (checkObjectNotEmpty(sublocationDatesFromState)) {
          setDateOfReading(sublocationDatesFromState?.dateOfReading);
          setDateEntered(sublocationDatesFromState?.dateEntered);
        }
      }
    }
  }, [sublocation]);

  useEffect(() => {
    if (readFromDraft && !isReconciliation) {
      const dates = draftSublocationDates[sublocation?.id];
      setDateOfReading(dates?.dateOfReading);
      setDateEntered(dates?.dateEntered);
    }
    if (readFromDraft && isReconciliation) {
      const dates = draftSublocationDatesOfReconciliation[sublocation?.id];
      setDateOfReading(dates?.dateOfReading);
      setDateEntered(dates?.dateEntered);
    }
  }, [readFromDraft, isReconciliation]);

  const stopIndicatorInteraction = (event) => {
    event.preventDefault();
    event.stopPropagation();
  };

  useEffect(() => {
    if (checkObjectNotEmpty(sublocation)) {
      if (!isReconciliation) {
        dispatch(
          setSublocationDates({
            [sublocation?.id]: {
              dateOfReading: dateFormatForApi(dateOfReading),
              dateEntered: dateFormatForApi(dateEntered),
            },
          }),
        );
      }
    }
  }, [sublocation]);

  const {
    currentData: sublocationData,
    error,
    isError,
    isSuccess,
  } = useGetCollectionsBySublocationIdQuery(
    {
      sublocationId: Number(sublocation?.id),
      dateOfReading: dateFormatForApi(dateOfReading || new Date()),
      transactionId: sublocation?.transactionId || '',
    },
    { skip: skip },
  );

  useEffect(() => {
    if (isSuccess && sublocationData) {
      const seedLiveStatesForUpdate = getSeedLiveDataForUpdate(
        sublocationData?.seedLiveData,
        originalSublocation,
        locationCurrency,
      );
      if (checkValueNotNullUndefinedBlank(seedLiveStatesForUpdate) && checkObjectNotEmpty(seedLiveStatesForUpdate)) {
        dispatch(setUpdateStatesForReconciliationSublocationDatesChange(seedLiveStatesForUpdate));
      }
      // Update assets from originalSublocation with new data (filters based on assetMeterDetails)
      const { updatedAssets } = getNewAssetDataFromSublocation(
        sublocationData?.assetHistory,
        sublocationData?.intercardTransaction,
        sublocationData?.assetMeterDetails,
      );

      // Check if the selected date is today's date or in the future
      const selectedDate = new Date(dateOfReading);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      selectedDate.setHours(0, 0, 0, 0);
      const isCurrentOrFutureDate = selectedDate >= today;

      // Store the updated assets in Redux so they're available for draft saving
      // For current/future dates, clear sublocationAssets to use original assets
      // For past dates, store the date-specific assets
      // Use different Redux slices for reconciliation vs regular collection
      if (isReconciliation) {
        if (isCurrentOrFutureDate) {
          // Clear sublocationAssets for current/future dates to use original assets
          dispatch(setSublocationAssetsForReconciliation({ [sublocation?.id]: null }));
        } else {
          // Store date-specific assets for past dates
          dispatch(setSublocationAssetsForReconciliation({ [sublocation?.id]: updatedAssets }));
        }
      } else {
        if (isCurrentOrFutureDate) {
          // Clear sublocationAssets for current/future dates to use original assets
          dispatch(setSublocationAssets({ [sublocation?.id]: null }));
        } else {
          // Store date-specific assets for past dates
          dispatch(setSublocationAssets({ [sublocation?.id]: updatedAssets }));
        }
      }

      const intercardDataForUpdate = getIntercardData([{ ...originalSublocation, assets: [...updatedAssets] }]);

      if (!isReconciliation) {
        const prevIntercardData = getPrevIntercardDataExcludingUpdatedIntercard(intercardData, updatedAssets);

        dispatch(setIntercardData({ ...prevIntercardData, ...intercardDataForUpdate }));

        const formikDataUpdates = generateFormikDataUpdates(updatedAssets, sublocationID, false);

        if (formikDataUpdates.length > 0) {
          dispatch(updateFormikDataForOpenCollections(formikDataUpdates));
        }
      }
      if (isReconciliation) {
        const prevIntercardData = getPrevIntercardDataExcludingUpdatedIntercard(
          intercardDataForReconciliation,
          updatedAssets,
        );
        const formikDataUpdates = generateFormikDataUpdates(updatedAssets, sublocationID, true);

        if (formikDataUpdates.length > 0) {
          dispatch(updateFormikData(formikDataUpdates));
        }

        dispatch(setIntercardDataForReconciliation({ ...prevIntercardData, ...intercardDataForUpdate }));
      }

      // Update location currency with date-specific exchange rate from backend
      if (sublocationData?.currency) {
        const currencyDetails = getCollectionCurrencyDetails(sublocationData.currency);
        const updatedLocationCurrency = {
          id: sublocationData.currency?.id || null,
          abbreviation: sublocationData.currency?.abbreviation || null,
          exchangeRateId: currencyDetails.exchangeRateId,
          exchangeRateDate: currencyDetails.exchangeRateDate,
          exchangeRate: currencyDetails.exchangeRate,
          exchangeRateForDisplay: currencyDetails.exchangeRateForDisplay,
        };
        if (isReconciliation) {
          dispatch(setLocationCurrencyForReconciliation(updatedLocationCurrency));
        } else {
          dispatch(setLocationCurrency(updatedLocationCurrency));
        }
      }

      // Update sublocation state with filtered assets
      setSublocation({
        ...sublocation,
        dateOfReading: dateOfReading,
        dateEntered: dateEntered,
        assets: [...updatedAssets],
      });

      // Notify parent to update searchRef with new assets and resync details for this sublocation
      // This triggers Formik reinitialization with proper validation and dirty tracking
      if (onSublocationAssetsUpdate) {
        onSublocationAssetsUpdate(sublocation.id, updatedAssets, sublocationData?.resync ?? []);
      }

      const getCurrentPlayCard = playCards[sublocation?.id];
      const playCardData = sublocationData?.playcardData[0];
      let initialPlaycardState = getInitialPlaycardState(playCardData);
      const prevPlaycardValues =
        checkObjectNotEmpty(playCards) && checkObjectNotEmpty(getCurrentPlayCard) ? getCurrentPlayCard : {};
      if (!isAllObjectValuesNullOrZero(playCardData) || sublocation?.isIntercardSublocation) {
        const revisedState = getRevisedPlaycardValuesWithChangeInDateOfReading(
          initialPlaycardState,
          prevPlaycardValues,
        );
        initialPlaycardState = {
          ...revisedState,
        };
      }
      const finalPlayCardCollection = {
        ...playCards,
        [sublocation?.id]: initialPlaycardState,
      };

      const totalPlayCardSales = getPlayCardSalesRevenueInUsd(finalPlayCardCollection);
      dispatch(setPlayCards({ ...playCards, [sublocation?.id]: initialPlaycardState }));
      dispatch(setPlayCardSalesSummary(totalPlayCardSales));
      if (accountSummary?.defaultAccountSummaryState) {
        dispatch(setDefaultAccountSummaryState(false));
      }
    } else if (isError && prevDateOfReading) {
      setDateOfReading(prevDateOfReading);
    }
  }, [sublocationData, isSuccess, isError]);

  const handleReadingDateChange = (value) => {
    if (isInteractionBlocked) {
      return;
    }

    const formattedDate = value ? getDateFormat(value) : null;
    setPrevDateOfReading(dateOfReading);
    setDateOfReading(formattedDate);
    // Only trigger API call if a valid date is selected (not cleared)
    if (value) {
      setSkip(false);
    }
    dispatch(setSubLocationDatesFormIsDirty(true));
    turnOffDraft();
  };
  let response = {};
  if (isNotIndependentRep) {
    response = useGetDatesOfReadingQuery({ sublocationId: sublocationID }, { skip: !shouldFetchHistory });
  }
  const { data } = response;
  const fetchCollectionDetails = (date, isManualNavigation = false) => {
    if (!data?.datesOfReadingData) return;
    if (!isManualNavigation && initialFromApproved) {
      setInitialFromApproved(false);
      return;
    }
    const normalizedDate = new Date(date)?.toISOString().split('T')[0];
    const collection = data.datesOfReadingData.find((d) => {
      const apiDate = new Date(d.date)?.toISOString().split('T')[0];
      return apiDate === normalizedDate;
    });

    if (collection) {
      const { transactionId } = collection;
      navigate(`/${routeConstants.COLLECTIONS_ROUTE}/approved/${transactionId}`, {
        replace: true,
      });
    }
  };

  const changeDate = (direction) => {
    if (isInteractionBlocked || !data?.datesOfReadingData?.length || !dateOfReading) return;

    saveAsDraftHandler();

    const currentDate = new Date(dateOfReading);
    let closestDate = null;

    if (direction === 'Left') {
      const earlierDates = data.datesOfReadingData.map((d) => new Date(d.date)).filter((d) => d < currentDate);
      closestDate = earlierDates.sort((a, b) => b - a)[0];
    } else {
      const laterDates = data.datesOfReadingData.map((d) => new Date(d.date)).filter((d) => d > currentDate);
      closestDate = laterDates.sort((a, b) => a - b)[0];
    }

    if (closestDate) {
      fetchCollectionDetails(closestDate?.toISOString(), true);
    }
  };

  const isLeftDisabled = useMemo(() => {
    if (!dateOfReading || !data?.datesOfReadingData?.length) return true;
    const currentDate = new Date(dateOfReading);
    return !data.datesOfReadingData.some((d) => new Date(d.date) < currentDate);
  }, [dateOfReading, data]);

  const isRightDisabled = useMemo(() => {
    if (!dateOfReading || !data?.datesOfReadingData?.length) return true;
    const currentDate = new Date(dateOfReading);
    return !data.datesOfReadingData.some((d) => new Date(d.date) > currentDate);
  }, [dateOfReading, data]);

  const handleDateEnteredChange = (value) => {
    if (isInteractionBlocked) {
      return;
    }

    setDateEntered(getDateFormat(value));
    dispatch(setSubLocationDatesFormIsDirty(true));
  };

  useEffect(() => {
    if (checkObjectNotEmpty(sublocation)) {
      if (!isReconciliation) {
        dispatch(
          setSublocationDates({
            [sublocation.id]: {
              dateOfReading: dateFormatForApi(dateOfReading),
              dateEntered: dateFormatForApi(dateEntered),
            },
          }),
        );
      }
      if (isReconciliation) {
        dispatch(
          setSublocationDatesForReconciliation({
            [sublocation.id]: {
              dateOfReading: dateFormatForApi(dateOfReading),
              dateEntered: dateFormatForApi(dateEntered),
            },
          }),
        );
      }
    }
  }, [dateEntered, dateOfReading]);

  const lastCollectionDate = getLastCollectionDate(sublocation, isReconciliation);
  return (
    <GridContainer lg={12} className={classes.dateContainer}>
      <GridItem xs={12} sm={12} md={12} lg={2} py={isBelowLG ? 1 : 0}>
        <Typography data-testid="accordionTitle" variant="subtitle5">
          {sublocation?.name}
        </Typography>
      </GridItem>
      <GridItem ml={2} style={{ cursor: 'pointer' }}>
        {isReconciliation && (
          <ChevronLeftIcon
            fontSize="large"
            onClick={(event) => {
              if (!isLeftDisabled && !isInteractionBlocked) {
                event.stopPropagation();
                event.preventDefault();
                changeDate('Left');
              }
            }}
            style={{
              cursor: isLeftDisabled || isInteractionBlocked ? 'not-allowed' : 'pointer',
              opacity: isLeftDisabled || isInteractionBlocked ? 0.3 : 1,
            }}
          />
        )}
      </GridItem>
      <GridItem
        xs={12}
        sm={3.5}
        md={4}
        lg={2}
        my={1}
        className={classes.datePointerEvent}
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
        }}
      >
        <Datepicker
          label={MESSAGE.DATE_OF_READING}
          name="dateOfReading"
          id="dateOfReading"
          onChange={handleReadingDateChange}
          value={dateOfReading}
          inputFormat="MM/DD/YYYY"
          disableFuture
          disabled={isInteractionBlocked}
          error={isError && error.status === 419}
          helperText={isError && error.status === 419 ? error.data.message : ''}
        />
      </GridItem>
      <GridItem mr={2} style={{ cursor: 'pointer' }}>
        {isReconciliation && (
          <ChevronRightIcon
            id="right"
            fontSize="large"
            onClick={(event) => {
              if (!isRightDisabled && !isInteractionBlocked) {
                event.stopPropagation();
                event.preventDefault();
                changeDate('Right');
              }
            }}
            style={{
              cursor: isRightDisabled || isInteractionBlocked ? 'not-allowed' : 'pointer',
              opacity: isRightDisabled || isInteractionBlocked ? 0.3 : 1,
            }}
          />
        )}
      </GridItem>
      <GridItem
        xs={12}
        sm={3.5}
        md={4}
        lg={2}
        mr={3}
        my={1}
        className={classes.datePointerEvent}
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
        }}
      >
        <Datepicker
          label={MESSAGE.DATE_ENTERED}
          name="dateEntered"
          onChange={handleDateEnteredChange}
          value={dateEntered}
          id="dateEntered"
          inputFormat="MM/DD/YYYY"
          disableFuture
          disabled={!isNotIndependentRep || isInteractionBlocked}
        />
      </GridItem>
      <GridItem xs={12} sm={3.5} md={3} lg={3}>
        <Typography variant="subtitle2">{`Last Collection - ${
          checkValueNotNullUndefinedBlank(lastCollectionDate) ? getDateFormat(lastCollectionDate) : 'N/A'
        }`}</Typography>
      </GridItem>
      {hasWarning && (
        <GridItem xs="auto" display="flex" alignItems="center">
          <button
            type="button"
            tabIndex={-1}
            aria-label={MESSAGE.VARIANCE_FOUND_TOOLTIP}
            onClick={stopIndicatorInteraction}
            onMouseDown={stopIndicatorInteraction}
            style={{
              display: 'inline-flex',
              padding: 0,
              border: 0,
              background: 'transparent',
              cursor: 'default',
            }}
          >
            <Tooltip
              title={MESSAGE.VARIANCE_FOUND_TOOLTIP}
              placement="top-end"
              enterDelay={50}
              leaveDelay={150}
              enterNextDelay={0}
              disableInteractive
              componentsProps={{
                popper: {
                  sx: {
                    '&[data-popper-placement*="top"] .MuiTooltip-tooltip': {
                      marginBottom: '2px !important',
                    },
                  },
                },
                tooltip: {
                  sx: {
                    bgcolor: '#fff',
                    color: '#111827',
                    border: '1px solid #b9c7d8',
                    boxShadow: '0 8px 20px rgba(15, 23, 42, 0.08)',
                    fontSize: '0.75rem',
                    fontWeight: 500,
                  },
                },
              }}
            >
              <Warning data-testid="sublocation-warning-icon" color="error" fontSize="small" />
            </Tooltip>
          </button>
        </GridItem>
      )}
      {hasDeleteSublocationAndAssetAccess && isReconciliation && (
        <GridItem xs={12} sm={3.5} md={3} lg={1}>
          <Button
            disabled={isInteractionBlocked}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleSublocationDeleteClick(sublocation.collectionId); // Handle the delete logic
            }}
          >
            delete
          </Button>
        </GridItem>
      )}
    </GridContainer>
  );
};

export default SublocationDateModule;
